import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Paper,
  Chip,
  Card,
  CardContent,
  CardActions,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Switch,
  Autocomplete,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import Search from '@mui/icons-material/Search';
import * as Icons from '@mui/icons-material';
import { FeeCategory, CrudPermissions } from '../../types';
import { useData } from '../../contexts/DataContext';

interface FeeCategoriesManagementProps {
  permissions: CrudPermissions;
}
import { v4 as uuidv4 } from 'uuid';

const AVAILABLE_ICONS = [
  'Category',
  'Favorite',
  'DirectionsBus',
  'School',
  'Explore',
  'ConfirmationNumber',
  'MoreHoriz',
  'AttachMoney',
  'CardGiftcard',
  'LocalLibrary',
  'MenuBook',
  'Class',
  'Science',
  'Calculate',
  'Palette',
  'MusicNote',
  'SportsBasketball',
  'SportsSoccer',
  'SportsFootball',
  'SportsBaseball',
  'Pool',
  'TheaterComedy',
  'Festival',
  'Celebration',
  'EventSeat',
  'LocalActivity',
  'ShoppingCart',
  'Store',
  'Receipt',
  'Payment',
  'CreditCard',
  'AccountBalance',
  'Paid',
  'Groups',
  'Group',
  'Person',
  'Badge',
  'Assignment',
  'AssignmentTurnedIn',
  'Book',
  'AutoStories',
  'ImportContacts',
  'Bookmark',
  'Backpack',
  'Luggage',
  'Checkroom',
  'Restaurant',
  'FastFood',
  'LocalDining',
  'Cake',
  'LocalCafe',
  'BusAlert',
  'DirectionsTransit',
  'CommuteTwoTone',
  'EmojiTransportation',
  'LocalParking',
  'Build',
  'Handyman',
  'Construction',
  'Engineering',
  'Computer',
  'Laptop',
  'PhoneAndroid',
  'Tablet',
  'Print',
  'Camera',
  'PhotoCamera',
  'Videocam',
  'Mic',
  'Headphones',
  'Biotech',
  'Psychology',
  'HealthAndSafety',
  'MedicalServices',
  'Vaccines',
  'LocalHospital',
  'Fitness',
  'SelfImprovement',
  'EmojiEvents',
  'Stars',
  'Star',
  'Redeem',
  'WorkspacePremium',
  'Collections',
  'Image',
  'BrokenImage',
  'Slideshow',
  'PublicOff',
  'Public',
  'Language',
  'Translate',
];

interface IconOption {
  name: string;
  Icon: React.ComponentType;
}

export const FeeCategoriesManagement: React.FC<FeeCategoriesManagementProps> = ({ permissions }) => {
  const { feeCategories: categories, setFeeCategories: setCategories } = useData();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<FeeCategory | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState<Partial<FeeCategory>>({
    name: '',
    description: '',
    icon: 'Category',
    color: '#2196f3',
    isActive: true,
  });

  const iconOptions = useMemo<IconOption[]>(() => {
    return AVAILABLE_ICONS.map(name => {
      const IconComponent = (Icons as any)[name];
      return {
        name,
        Icon: IconComponent || Icons.Category,
      };
    });
  }, []);

  const filteredCategories = useMemo(() => {
    return categories.filter((category) => {
      const matchesSearch =
        category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        category.description.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesSearch;
    });
  }, [categories, searchQuery]);

  const handleAdd = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      icon: 'Category',
      color: '#2196f3',
      isActive: true,
    });
    setOpenDialog(true);
  };

  const handleEdit = (category: FeeCategory) => {
    setEditingCategory(category);
    setFormData(category);
    setOpenDialog(true);
  };

  const handleDelete = (id: string) => {
    if (id === '5') {
      alert('Event Tickets category cannot be deleted as it is a system category.');
      return;
    }
    if (window.confirm('Are you sure you want to delete this category?')) {
      setCategories(categories.filter((c) => c.id !== id));
      const index = mockFeeCategories.findIndex((c) => c.id === id);
      if (index > -1) {
        mockFeeCategories.splice(index, 1);
      }
    }
  };

  const handleSave = () => {
    if (editingCategory) {
      const updated = categories.map((c) =>
        c.id === editingCategory.id ? { ...c, ...formData } : c
      );
      setCategories(updated);
      const index = mockFeeCategories.findIndex((c) => c.id === editingCategory.id);
      if (index > -1) {
        mockFeeCategories[index] = { ...mockFeeCategories[index], ...formData } as FeeCategory;
      }
    } else {
      const newCategory: FeeCategory = {
        id: uuidv4(),
        name: formData.name!,
        description: formData.description!,
        icon: formData.icon,
        color: formData.color,
        isActive: formData.isActive!,
      };
      setCategories([...categories, newCategory]);
      mockFeeCategories.push(newCategory);
    }
    setOpenDialog(false);
  };

  const getIcon = (iconName?: string) => {
    if (!iconName) return <Icons.Category />;
    const IconComponent = (Icons as any)[iconName];
    return IconComponent ? <IconComponent /> : <Icons.Category />;
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 3,
      }}>
        <Typography variant="h5" fontWeight={600}>
          Fee Categories Management
        </Typography>
        {permissions.create && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAdd}
            sx={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              },
              flexShrink: 0,
            }}
          >
            Add Category
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          placeholder="Search categories..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          },
          gap: { xs: 2, sm: 3 },
        }}
      >
        {filteredCategories.map((category) => (
          <Box
            key={category.id}
            sx={{
              display: 'flex',
              minWidth: 0,
            }}
          >
            <Card
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1.5,
                      backgroundColor: category.color || '#2196f3',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                    }}
                  >
                    {getIcon(category.icon)}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
                      {category.name}
                    </Typography>
                    <Chip
                      label={category.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={category.isActive ? 'success' : 'default'}
                      sx={{ height: 20, fontSize: '0.75rem' }}
                    />
                  </Box>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {category.description}
                </Typography>
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                {permissions.update && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => handleEdit(category)}
                    sx={{ flexGrow: 1, minWidth: 0 }}
                  >
                    Edit
                  </Button>
                )}
                {permissions.delete && (
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(category.id)}
                    disabled={category.id === '5'}
                    color="error"
                    sx={{ flexShrink: 0 }}
                  >
                    <Delete />
                  </IconButton>
                )}
              </CardActions>
            </Card>
          </Box>
        ))}
      </Box>

      {filteredCategories.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No categories found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchQuery
              ? 'Try adjusting your search'
              : 'Click "Add Category" to create your first category'}
          </Typography>
        </Box>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory ? 'Edit Category' : 'Add New Category'}
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '60vh', overflow: 'auto' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Category Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
              required
            />
            <Autocomplete
              options={iconOptions}
              value={iconOptions.find(opt => opt.name === formData.icon) || null}
              onChange={(_, newValue) => {
                setFormData({ ...formData, icon: newValue?.name || 'Category' });
              }}
              getOptionLabel={(option) => option.name}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <option.Icon />
                  <Typography>{option.name}</Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Icon"
                  placeholder="Search icons..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: formData.icon && (
                      <InputAdornment position="start">
                        {(() => {
                          const IconComponent = (Icons as any)[formData.icon];
                          return IconComponent ? <IconComponent /> : <Icons.Category />;
                        })()}
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              fullWidth
            />
            <TextField
              label="Color"
              type="color"
              value={formData.color}
              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.name || !formData.description}
            sx={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
